import { graphql } from "@/config/graphql";
import { useMutation, useQuery } from "@apollo/client";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  DialogActions,
  InputAdornment,
  TextField,
  Typography,
  styled,
} from "@mui/material";
import { useParams } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { formatPlate } from "@/utils/plate";
import { ChangeEvent, useEffect } from "react";

const Content = styled(Box)`
  padding: 16px 24px;
`;
const Form = styled("form")`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 16px;
`;

const lastTransportationInfoQuery = graphql(`
  query lastTransportationInfo {
    lastTransportationInfo {
      plate
      odometer
      driver
    }
    drivers
  }
`);

const addTransportationControlDetailMutation = graphql(`
  mutation addTransportationControlDetail(
    $params: AddTransportationControlDetailParams!
  ) {
    addTransportationControlDetail(params: $params) {
      nextModified
      report {
        id
        transportationControlDetails {
          id
          plate
          diesel
          lastOdometer
          chargeOdometer
          kms
          performance
          driver
        }
      }
    }
  }
`);
const updateTransportationControlDetailMutation = graphql(`
  mutation updateTransportationControlDetail(
    $params: UpdateTransportationControlDetailParams!
  ) {
    updateTransportationControlDetail(params: $params) {
      nextModified
      report {
        id
        transportationControlDetails {
          id
          plate
          diesel
          lastOdometer
          chargeOdometer
          kms
          performance
          driver
        }
      }
    }
  }
`);
const removeTransportationControlDetailMutation = graphql(`
  mutation removeTransportationControlDetail(
    $params: RemoveTransportationControlDetailParams!
  ) {
    removeTransportationControlDetail(params: $params) {
      nextModified
      report {
        id
        transportationControlDetails {
          id
          plate
          diesel
          lastOdometer
          chargeOdometer
          kms
          performance
          driver
        }
      }
    }
  }
`);

const transportationControlDetailSchema = z
  .object({
    id: z.number().int().optional(),
    plate: z.string().refine(
      (value) => {
        if (/^(?:[A-Z]{2}-\d{4}|[A-Z]{4}-\d{2})$/.exec(value)) return true;
        return false;
      },
      { message: "Ingrese una patente válida (XX-0000) o (XXXX-00)" }
    ),
    diesel: z.coerce
      .number()
      .finite()
      .gte(0, "Debes ingresar un valor mayor a 0")
      .max(800, "La cantidad máxima de diesel es 800 lt"),
    lastOdometer: z.coerce.number().finite().gte(0),
    chargeOdometer: z.coerce.number().finite().gte(0),
    driver: z.string().optional(),
  })
  .refine(
    (data) => {
      return data.chargeOdometer >= data.lastOdometer;
    },
    {
      message: "El odómetro de carga debe ser mayor al último odómetro",
      path: ["chargeOdometer"],
    }
  );
type TransportationControlDetailSchema = z.infer<
  typeof transportationControlDetailSchema
>;
interface TransportationControlReportFormProps {
  close: () => void;
  defaultValues?: Partial<TransportationControlDetailSchema>;
  usedPlates: string[];
}
export const TransportationControlReportForm = (
  props: TransportationControlReportFormProps
) => {
  const params = useParams();
  const { data: lastTransportationInfo } = useQuery(
    lastTransportationInfoQuery,
    { fetchPolicy: "cache-and-network" }
  );
  const [addTransportationControlDetail, { loading: adding }] = useMutation(
    addTransportationControlDetailMutation
  );
  const [updateTransportationControlDetail, { loading: updating }] =
    useMutation(updateTransportationControlDetailMutation);
  const [removeTransportationControlDetail, { loading: removing }] =
    useMutation(removeTransportationControlDetailMutation, {
      variables: {
        params: {
          id: props.defaultValues?.id || 0,
        },
      },
    });
  const loading = adding || updating || removing;
  const { control, handleSubmit, watch, setValue } =
    useForm<TransportationControlDetailSchema>({
      resolver: zodResolver(transportationControlDetailSchema),
      defaultValues: props.defaultValues,
    });
  const [lastOdometer, chargeOdometer, diesel, plate] = watch([
    "lastOdometer",
    "chargeOdometer",
    "diesel",
    "plate",
  ]);

  useEffect(
    function setPreviousDataForPlate() {
      if (plate && plate === props.defaultValues?.plate) {
        if (props.defaultValues?.lastOdometer)
          setValue("lastOdometer", props.defaultValues?.lastOdometer);

        setValue("driver", props.defaultValues?.driver ?? "");
        return;
      }
      if (lastTransportationInfo && plate) {
        const lastInfo = lastTransportationInfo.lastTransportationInfo.find(
          (info) => info.plate === plate
        );
        if (lastInfo?.odometer) setValue("lastOdometer", lastInfo.odometer);
        if (lastInfo?.driver) setValue("driver", lastInfo.driver);
      }
    },
    [
      lastTransportationInfo,
      plate,
      props.defaultValues?.driver,
      props.defaultValues?.lastOdometer,
      props.defaultValues?.plate,
      setValue,
    ]
  );

  const kilometers =
    lastOdometer && chargeOdometer ? chargeOdometer - lastOdometer : undefined;
  const performance = kilometers && diesel ? kilometers / diesel : undefined;
  const handleClose = () => props.close();
  const handleRemove = async () => {
    await removeTransportationControlDetail();
    props.close();
  };
  const onSubmit = async (data: TransportationControlDetailSchema) => {
    if (props.defaultValues?.id) {
      await updateTransportationControlDetail({
        variables: {
          params: {
            id: props.defaultValues.id,
            ...data,
          },
        },
      });
    } else {
      await addTransportationControlDetail({
        variables: {
          params: {
            ...data,
            reportId: +(params.id || 0),
          },
        },
      });
    }
    handleClose();
  };

  const plates =
    lastTransportationInfo?.lastTransportationInfo
      .map((info) => info.plate)
      .filter((plate) => {
        if (plate === props.defaultValues?.plate) return true;
        if (props.usedPlates.includes(plate)) return false;
        return true;
      }) || [];
  const drivers = lastTransportationInfo?.drivers ?? [];

  return (
    <>
      <Content>
        <Typography variant="h6">Agregar Transporte</Typography>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Controller
            name="plate"
            control={control}
            render={({ field, fieldState }) => (
              <Autocomplete
                options={plates}
                value={field.value ?? ""}
                onChange={(_, value) => {
                  field.onChange(value);
                }}
                freeSolo
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Patente"
                    fullWidth
                    inputProps={{
                      ...params.inputProps,
                      value: field.value ?? "",
                      onChange: (e: ChangeEvent<HTMLInputElement>) => {
                        params.inputProps.onChange?.(e);
                        field.onChange(formatPlate(e.target.value));
                      },
                    }}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              ></Autocomplete>
            )}
          />
          <Controller
            name="driver"
            control={control}
            render={({ field, fieldState }) => (
              <Autocomplete
                options={drivers}
                value={field.value ?? ""}
                onChange={(_, value) => {
                  field.onChange(value);
                  console.log({ value });
                }}
                freeSolo
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Conductor"
                    fullWidth
                    inputProps={{
                      ...params.inputProps,
                      value: field.value ?? "",
                      onChange: (e: ChangeEvent<HTMLInputElement>) => {
                        params.inputProps.onChange?.(e);
                        field.onChange(e.target.value);
                      },
                    }}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              ></Autocomplete>
            )}
          />
          <Controller
            name="diesel"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                label="Diesel"
                fullWidth
                type="number"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                {...field}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">lt</InputAdornment>
                  ),
                }}
              ></TextField>
            )}
          />
          <Controller
            name="lastOdometer"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                label="Último Odómetro"
                fullWidth
                type="number"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">km</InputAdornment>
                  ),
                }}
                {...field}
                value={field.value ?? ""}
              ></TextField>
            )}
          />
          <Controller
            name="chargeOdometer"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                label="Odómetro Carga"
                fullWidth
                type="number"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">km</InputAdornment>
                  ),
                }}
                {...field}
              ></TextField>
            )}
          />

          <Typography fontWeight={800}>
            Kilómetros:{" "}
            <Typography display={"inline"}>
              {kilometers ? kilometers.toLocaleString("es-CL") : "Por calcular"}
            </Typography>
          </Typography>

          <Typography fontWeight={800}>
            Rendimiento (km/lt):{" "}
            <Typography display={"inline"}>
              {performance
                ? performance.toLocaleString("es-CL")
                : "Por calcular"}
            </Typography>
          </Typography>
        </Form>
      </Content>
      <DialogActions sx={{ padding: "8px 24px" }}>
        {props.defaultValues?.id ? (
          <>
            <Button disabled={loading} onClick={handleRemove} color="error">
              ELIMINAR
              {removing && (
                <CircularProgress size={20} sx={{ marginLeft: 1 }} />
              )}
            </Button>
            <Button
              disabled={loading}
              onClick={handleSubmit(onSubmit)}
              color="info"
            >
              GUARDAR
              {updating && (
                <CircularProgress size={20} sx={{ marginLeft: 1 }} />
              )}
            </Button>
          </>
        ) : (
          <>
            <Button disabled={loading} onClick={handleClose} color="error">
              CANCELAR
            </Button>
            <Button
              disabled={loading}
              onClick={handleSubmit(onSubmit)}
              color="info"
            >
              GUARDAR
              {adding && <CircularProgress size={20} sx={{ marginLeft: 1 }} />}
            </Button>
          </>
        )}
      </DialogActions>
    </>
  );
};
